import React, { useState } from "react";
import PropTypes from "prop-types";
import styled, { withTheme } from "styled-components";
import Pill from "components/Pill";
import Image from "next/image";
import Link from "next/link";
import { toast } from "react-toastify";
import generateRandomImagePlaceholderPath from "lib/generate-random-image-placeholder";
import { calculateTime, formatCalDate } from "lib/date-helpers";
import {
  OuterWrapper,
  ContentWrapper,
  Subtitle,
  TopLineBreak,
  DateSection,
  Number,
  Day,
  Month,
  BookingInfo,
  Details,
  Name,
  SmallText,
  BizImage,
  ServicePill,
  PillContent,
  SectionWrapper,
  InfoSection,
  PetSection,
  PetImageInitialWrapper,
  PetNamePillContent,
  PetName,
  ServicesSection,
  ServicePillWrapper,
  ServiceLength,
  TopSection,
  PillWrapper,
  BottomSection,
  Total,
  TotalWrap,
  PriceItem,
  Initials,
  ToggleDetails,
  LoadingOverlay,
  LoadingBackground,
  AppointmentConfirm,
  ApptSmallText,
  ButtonContainer,
  DeclineButton,
  RescheduleButton,
  ConfirmButton
} from "components/Shared/card";
import { toastSettings, TIPPING_WINDOW } from "lib/constants";
import Spinner from "components/Spinner";

const TipWrap = styled(TotalWrap)`
  margin: 1.25rem 1rem 0 0;
  width: 5rem;
`;

function AppointmentCard({
  id,
  day,
  number,
  month,
  businessName,
  businessAddress,
  tippingCompleted,
  endDatetime,
  pillTitle,
  pillType,
  staffName,
  service,
  totalAmountInt,
  showModal,
  petName,
  petImage,
  bizId,
  bizImage,
  type,
  status,
  onClick,
  rescheduleLink,
  paymentLink,
  form,
  fieldIndex,
  handleSubmit,
  reload,
  theme,
  createdAtTime,
  apptStartTime,
  genPending,
  setShowModal,
}) {
  const [expanded, setExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const initials = petName[0]?.toUpperCase();
  const tip = () => {
    const now = new Date();
    const limit = new Date(
      new Date(endDatetime).getTime() + TIPPING_WINDOW * 24 * 60 * 60 * 1000
    );
    if (now < limit && !tippingCompleted && status === "completed") {
      return (
        <TipWrap>
          <Link href={`appointments/${id}/tip`}>Leave a tip</Link>
        </TipWrap>
      );
    }
    return null;
  };

  const updateAppointment = async (e, apptStatus) => {
    e.stopPropagation();
    try {
      setIsLoading(true);
      form.change(
        `appointments.data[${fieldIndex}].attributes.status`,
        apptStatus
      );
      const res = await handleSubmit();
      if (res instanceof Error) {
        throw res;
      }
      await reload();
      setIsLoading(false);
      toast.success(`Appointment ${apptStatus}`, toastSettings);
      if (showModal) setShowModal(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error(
        "Could not update appointment, please try again",
        toastSettings
      );
    }
  };
  return (
    <OuterWrapper onClick={onClick} showModal={showModal}>
      <ContentWrapper showPointer={!showModal}>
        {isLoading && (
          <LoadingOverlay>
            <LoadingBackground />
            <Spinner
              color={theme.themeColors.primary}
              overridePositionAbsolute
            />
          </LoadingOverlay>
        )}
        <DateSection>
          <Day>{day}</Day>
          <Number>{number}</Number>
          <Month> {month}</Month>
        </DateSection>
        <TopSection showModal={showModal}>
          <PillWrapper>
            <Pill status={status} type={pillType} title={pillTitle} />
          </PillWrapper>
        </TopSection>
        <TopLineBreak />
        <BookingInfo>
          <BizImage
            alt={`Image of ${businessName}`}
            height={42}
            objectFit="cover"
            src={bizImage || generateRandomImagePlaceholderPath(bizId, type)}
            width={56}
          />
          <Details>
            <Name>{businessName}</Name>
            <SmallText>{businessAddress}</SmallText>
          </Details>
        </BookingInfo>
        {genPending().status && (
          <AppointmentConfirm expanded={expanded}>
            <ApptSmallText>
              Submitted on {formatCalDate(createdAtTime)} (
              {calculateTime(apptStartTime)})
            </ApptSmallText>
            <ButtonContainer>
              {genPending().reason.rescheduling && (
                <Link href={rescheduleLink} passHref>
                  <RescheduleButton
                    onClick={async (e) => {
                      e.stopPropagation();
                    }}
                  >
                    Reschedule
                  </RescheduleButton>
                </Link>
              )}
              {genPending().reason.customerApproval && (
                <>
                  <DeclineButton
                    onClick={async (e) => {
                      updateAppointment(e, "declined");
                    }}
                  >
                    Decline
                  </DeclineButton>
                  <Link href={paymentLink} passHref>
                    <ConfirmButton
                      colorType="success"
                      onClick={async (e) => {
                        e.stopPropagation();
                      }}
                    >
                      Confirm
                    </ConfirmButton>
                  </Link>
                </>
              )}
            </ButtonContainer>
          </AppointmentConfirm>
        )}
        <SectionWrapper showModal={showModal}>
          <InfoSection expanded={expanded} showModal={showModal}>
            <Subtitle>STAFF</Subtitle>
            <ServicePill>
              <PillContent>{staffName || "None Assigned"}</PillContent>
            </ServicePill>
          </InfoSection>
          <PetSection expanded={expanded} showModal={showModal}>
            <Subtitle>PET</Subtitle>
            <ServicePill>
              <PetNamePillContent>
                <PetImageInitialWrapper>
                  {(petImage && (
                    <Image
                      alt={`Image of ${petName}`}
                      height={26}
                      objectFit="cover"
                      src={petImage}
                      width={26}
                    />
                  )) || <Initials>{initials}</Initials>}
                </PetImageInitialWrapper>
                <PetName>{petName}</PetName>
              </PetNamePillContent>
            </ServicePill>
          </PetSection>
          {!showModal && (
            <>
              <ServicesSection expanded={expanded}>
                <Subtitle>REASON</Subtitle>
                <ServicePillWrapper>
                  <ServicePill>
                    <PillContent>{service}</PillContent>
                  </ServicePill>
                  <ServiceLength>
                    {[service].length > 1 && `+${[service].length - 1} more`}
                  </ServiceLength>
                </ServicePillWrapper>
              </ServicesSection>
            </>
          )}
        </SectionWrapper>
        {!showModal && (
          <BottomSection expanded={expanded}>
            {tip()}
            <TotalWrap>
              <Total>TOTAL</Total>
              <PriceItem value={totalAmountInt / 100} />
            </TotalWrap>
          </BottomSection>
        )}
      </ContentWrapper>
      <ContentWrapper expanded={expanded}>
        {!showModal && (
          <ToggleDetails
            expanded={expanded}
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(!expanded);
            }}
          >
            {expanded ? "Hide details" : "Show details"}
          </ToggleDetails>
        )}
      </ContentWrapper>
    </OuterWrapper>
  );
}

export default withTheme(AppointmentCard);

AppointmentCard.propTypes = {
  day: PropTypes.string,
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  month: PropTypes.string,
  businessName: PropTypes.string,
  businessAddress: PropTypes.string,
  bizId: PropTypes.string,
  bizImage: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  endDatetime: PropTypes.string,
  id: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  petImage: PropTypes.string,
  petName: PropTypes.string,
  pillType: PropTypes.string,
  pillTitle: PropTypes.string,
  service: PropTypes.string,
  showModal: PropTypes.bool,
  staffName: PropTypes.string,
  status: PropTypes.string,
  tippingCompleted: PropTypes.string,
  totalAmountInt: PropTypes.number,
  type: PropTypes.arrayOf(PropTypes.shape({})),
  rescheduleLink: PropTypes.string,
  paymentLink: PropTypes.string,
  genPending: PropTypes.func,
  form: PropTypes.shape({
    change: PropTypes.func,
  }),
  fieldIndex: PropTypes.number,
  handleSubmit: PropTypes.func,
  reload: PropTypes.func,
  theme: PropTypes.shape({
    themeColors: PropTypes.shape({
      primary: PropTypes.string,
    }),
  }),
  createdAtTime: PropTypes.string,
  apptStartTime: PropTypes.string,
  setShowModal: PropTypes.func,
};

AppointmentCard.defaultProps = {
  day: "",
  number: "",
  month: "",
  businessName: "",
  businessAddress: "",
  bizId: "",
  bizImage: undefined,
  endDatetime: null,
  type: undefined,
  pillType: "",
  pillTitle: "",
  staffName: "",
  service: "",
  totalAmountInt: 0,
  showModal: false,
  petName: "",
  petImage: undefined,
  status: "",
  onClick: () => {},
  rescheduleLink: "/",
  paymentLink: "/",
  genPending: () => {},
  form: undefined,
  fieldIndex: undefined,
  handleSubmit: undefined,
  reload: undefined,
  theme: {},
  tippingCompleted: null,
  createdAtTime: null,
  apptStartTime: null,
  setShowModal: () => {},
};
